import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface Notification {
  id: string;
  title: string;
  body?: string;
  type: 'info' | 'success' | 'warning' | 'alert';
  read: boolean;
  created_at: string;
}

interface NotificationWidgetProps {
  config: Record<string, unknown>;
  data?: Notification[];
  onNotificationClick?: (notification: Notification) => void;
}

const TYPE_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertCircle,
  alert: Bell,
};

const TYPE_COLORS = {
  info: 'text-blue-500',
  success: 'text-green-500',
  warning: 'text-yellow-500',
  alert: 'text-red-500',
};

export function NotificationWidget({ config, data = [], onNotificationClick }: NotificationWidgetProps) {
  const title = (config.title as string) || 'Notifications';
  const maxItems = (config.maxItems as number) || 5;
  const showUnreadOnly = config.showUnreadOnly === true;
  const emptyMessage = (config.emptyMessage as string) || 'No notifications';

  const filteredNotifications = showUnreadOnly 
    ? data.filter(n => !n.read)
    : data;

  const displayedNotifications = filteredNotifications.slice(0, maxItems);
  const unreadCount = data.filter(n => !n.read).length;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Bell className="h-4 w-4" />
            {title}
          </CardTitle>
          {unreadCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {unreadCount} new
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {displayedNotifications.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {emptyMessage}
          </p>
        ) : (
          displayedNotifications.map((notification) => {
            const Icon = TYPE_ICONS[notification.type] || Info;
            const colorClass = TYPE_COLORS[notification.type] || 'text-muted-foreground';
            
            return (
              <div
                key={notification.id}
                className={`p-2 rounded-lg transition-colors cursor-pointer ${
                  notification.read 
                    ? 'bg-muted/30 hover:bg-muted/50' 
                    : 'bg-primary/5 hover:bg-primary/10'
                }`}
                onClick={() => onNotificationClick?.(notification)}
              >
                <div className="flex gap-2">
                  <Icon className={`h-4 w-4 flex-shrink-0 mt-0.5 ${colorClass}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${notification.read ? '' : 'font-medium'}`}>
                      {notification.title}
                    </p>
                    {notification.body && (
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {notification.body}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                  )}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
